import { useParams, Link } from 'react-router-dom';
import { categories, getComicsByCategory } from '@/mocks/comics';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { useEffect, useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const comics = slug ? getComicsByCategory(slug) : [];
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [activeSlug, setActiveSlug] = useState(slug || '');

  useEffect(() => {
    setActiveSlug(slug || '');
  }, [slug]);

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === 'latest') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    if (sortBy === 'popular') return b.views - a.views;
    return b.rating - a.rating;
  });

  if (!currentCategory) {
    return (
      <div className="pt-24 pb-20 text-center px-4">
        <SEOHead title="分类未找到 - 韩漫网" description="您访问的分类页面不存在。" />
        <h2 className="font-heading text-2xl text-foreground-800">该分类不存在</h2>
        <Link to="/" className="inline-block mt-4 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">返回首页</Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}韩国漫画 - 免费${currentCategory.name}韩漫在线阅读 - 韩漫网`}
        description={`韩漫网${currentCategory.name}分类专区，收录${currentCategory.count}部热门${currentCategory.name}题材韩国漫画，免费在线阅读，每日更新韩国${currentCategory.name}漫画最新章节。`}
        keywords={`${currentCategory.name}韩漫,${currentCategory.name}韩国漫画,免费${currentCategory.name}漫画`}
        canonicalUrl={`https://hanman.readdy.ai/category/${slug}`}
        ogType="website"
      />
      <SchemaOrg
        type="WebPage"
        name={`${currentCategory.name}韩国漫画分类 - 韩漫网`}
        description={`韩漫网${currentCategory.name}分类，收录${currentCategory.count}部热门${currentCategory.name}题材韩国漫画`}
        url={`https://hanman.readdy.ai/category/${slug}`}
      />

      <div className="pt-20">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: '分类' },
          { label: currentCategory.name },
        ]} />

        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                <i className={`${currentCategory.icon} text-xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  {currentCategory.name}
                  <span className="text-base font-normal text-foreground-400 ml-2">韩国漫画</span>
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {currentCategory.count} 部作品
                </p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-56 flex-shrink-0">
                <nav className="sticky top-24">
                  <h3 className="text-sm font-medium text-foreground-400 mb-3 uppercase tracking-wide">全部分类</h3>
                  <div className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        onClick={() => setActiveSlug(cat.slug)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer ${
                          activeSlug === cat.slug
                            ? 'bg-primary-50 text-primary-600 font-medium'
                            : 'text-foreground-600 hover:bg-secondary-50 hover:text-foreground-800'
                        }`}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${cat.icon} text-sm`}></i>
                        </div>
                        {cat.name}
                        <span className="text-xs text-foreground-400 ml-auto">{cat.count}</span>
                      </Link>
                    ))}
                  </div>
                </nav>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-700">{comics.length}</strong> 部作品
                  </p>
                  <div className="flex items-center bg-secondary-100 rounded-full p-1">
                    {[
                      { key: 'latest', label: '最新' },
                      { key: 'popular', label: '人气' },
                      { key: 'rating', label: '评分' },
                    ].map((item) => (
                      <button
                        key={item.key}
                        onClick={() => setSortBy(item.key as typeof sortBy)}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                          sortBy === item.key
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {sortedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}